import { Location } from '@angular/common';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, Observable, switchMap, throwError } from 'rxjs';
import { ApiConstants } from '../api/api.constant';
import { AppConstants } from '../app.constants';
import { SessionService } from '../services/session.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    private readonly location = inject(Location);
    private readonly sessionService = inject(SessionService);

    /**
     * Intercept any http request.
     * If we do requests to Amazon we can't intercept, so continue as usual.
     * Else
     * Listen for errors on any http request and see if it's an 401 else continue as usual.
     * If 401 we try to call our `/authenticate` end point to retrieve a new access token.
     * The retry the earlier failed request
     *
     * @param {HttpRequest<any>} request
     * @param {HttpHandler} next
     * @returns {Observable<HttpEvent<any>>}
     */
    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // TODO: Remove this and fix this per request in the services instead
        // This prevents us in sending the access token to Amazon (e.g. uploading an image)
        const isException =
            request.url.includes(AppConstants.AMAZON_AWS_REQUEST) || request.url.includes(AppConstants.HEARTBEAT_URL) || request.url.includes('auth/register');
        if (isException) {
            return next.handle(request);
        }

        // Listen for errors on the requests
        return next.handle(request).pipe(
            catchError((error: any) => {
                // Ignore certain end points
                const ignoreCalls = [
                    `/${ApiConstants.API_GROUP_USERS}/${ApiConstants.API_METHOD_LOGIN}`,
                    `/${ApiConstants.API_GROUP_OAUTH}/${ApiConstants.API_METHOD_TOKEN}/${ApiConstants.API_METHOD_REFRESH}`,
                    ApiConstants.API_GROUP_EXCHANGE_AUTH_TOKEN,
                    `/${ApiConstants.API_GROUP_OAUTH}/${ApiConstants.API_METHOD_EXCHANGE}`
                ];
                if (ignoreCalls.some((path) => request.url.includes(path))) {
                    return throwError(() => error);
                }

                // We only listen for the unauthorized response
                if (error.status === 401) {
                    return this.sessionService.refreshAccessToken().pipe(
                        // The shared refresh request has completed, so retry the original request.
                        switchMap(() => next.handle(request)),
                        catchError((refreshError: unknown) => {
                            this.sessionService.handleRefreshFailure(refreshError, this.location.path());
                            return EMPTY;
                        })
                    );
                }
                // All other kind of errors are ignored (rethrow the error) and let the caller handle the error as usual.
                return throwError(() => error);
            })
        );
    }
}
